//---------------------------------------------------------
// HA pair copy and view logic (UX lock for #50 and #51). Every user-facing
// string for the pair lives here so the vitest suite can pin it. Components
// only render what these functions return.
//---------------------------------------------------------
import type {HAJobStep, HAJobStepName, HANodeHealth, HANodeIndex, HAPairDetail, HAPairHealth, HAPairNode, HASaveResult, HASyncState} from 'types/haPair';

export const HA_CHIP_LABEL = 'HA';
/** Outlined HA chip: 12px, weight 600, #113351, 4px radius. */
export const HA_CHIP_STYLE = {fontSize: 12, fontWeight: 600, color: '#113351', borderColor: '#113351', borderRadius: '4px'} as const;
export const HA_AMBER = '#B26A00';

//---------------------------------------------------------
// Tile
//---------------------------------------------------------
export type ChipTone = 'success' | 'warning' | 'error';
export function pairHealthChip(h: HAPairHealth | string): {label: string; tone: ChipTone} {
	if (h === 'healthy') return {label: 'Healthy', tone: 'success'};
	if (h === 'degraded') return {label: 'Degraded', tone: 'warning'};
	return {label: 'Down', tone: 'error'};
}

export const FAILED_OVER_CHIP = 'Failed over to node 2';
export function failedOverChip(failedOver: boolean): string | null {
	return failedOver ? FAILED_OVER_CHIP : null;
}

/** The pair tile shows Settings and Delete only. No power or reboot. */
export const PAIR_TILE_ACTIONS = ['settings', 'delete'] as const;

//---------------------------------------------------------
// Detail
//---------------------------------------------------------
export const NODE_COLUMNS = ['Node', 'Role', 'Holds VIP', 'Health', 'Public IP', 'Private IP', 'Linode ID', 'Settings', ''] as const;
export const HOLDS_VIP_CHIP = 'Holds VIP';
export const CANT_TELL_VIP = "Can't tell which node holds the VIP.";

/** Role comes from the BIRD config the job wrote. Never LoxiLB MASTER/BACKUP. */
export function roleLabel(role: string): string {
	return role === 'primary' ? 'Primary' : 'Secondary';
}

export function nodeHealthLabel(h: HANodeHealth | string): string {
	if (h === 'up') return 'Up';
	if (h === 'down') return 'Down';
	return 'Unknown';
}

export function syncLabel(s: HASyncState | string): string {
	switch (s) {
		case 'in_sync':
			return 'In sync';
		case 'saving':
			return 'Saving…';
		case 'unreachable':
			return 'Not reachable';
		default:
			return 'Out of sync';
	}
}

/** Which row shows the Holds VIP chip, or null for "can't tell". */
export function vipHolder(d: Pick<HAPairDetail, 'vipHolder' | 'nodes'>): HANodeIndex | null {
	if (d.vipHolder !== 1 && d.vipHolder !== 2) return null;
	const n1 = d.nodes.find(n => n.node === 1);
	const n2 = d.nodes.find(n => n.node === 2);
	if (!n1 || !n2 || n1.holdsVip === null || n2.holdsVip === null) return null;
	return d.vipHolder;
}

export function headerLine(d: Pick<HAPairDetail, 'vip' | 'mode' | 'region'>): {vip: string; mode: string; region: string} {
	return {vip: d.vip, mode: d.mode, region: d.region};
}

/** Non-dismissable warning while node 2 holds the VIP. */
export function failoverWarning(d: Pick<HAPairDetail, 'failedOver' | 'vipHolder' | 'nodes'>): string | null {
	if (!d.failedOver || vipHolder(d) !== 2) return null;
	const n1 = d.nodes.find(n => n.node === 1);
	return `Node 2 holds the VIP. Node 1 is ${nodeHealthLabel(n1?.health ?? 'unknown').toLowerCase()}.`;
}

export function linodeIdCell(n: HAPairNode): string {
	return n.linodeId != null ? String(n.linodeId) : '—';
}

//---------------------------------------------------------
// Save to both nodes
//---------------------------------------------------------
export const SAVE_LABEL = 'Save';
export const SAVING_LABEL = 'Saving to both nodes…';
export const SAVED_TOAST = 'Saved to both nodes.';

export function retryLabel(node: HANodeIndex): string {
	return `Retry node ${node}`;
}

export function unreachableWarning(node: HANodeIndex): string {
	return `Node ${node} isn't reachable. Changes are off until both nodes answer, so they stay the same.`;
}

/** The first unreachable node, if any. Save is off while one exists. */
export function unreachableNode(nodes: HAPairNode[]): HANodeIndex | null {
	const n = [...nodes].sort((a, b) => a.node - b.node).find(x => x.syncState === 'unreachable');
	return n ? n.node : null;
}

export function saveDisabled(args: {saving: boolean; canWrite: boolean; nodes: HAPairNode[]; state: string}): boolean {
	return args.saving || !args.canWrite || args.state !== 'active' || unreachableNode(args.nodes) !== null;
}

export function partialSaveMessage(savedNode: HANodeIndex | null, failedNode: HANodeIndex, reason: string): string {
	const tail = `Node ${failedNode} didn't take the change: ${reason}. The nodes don't match.`;
	return savedNode ? `Saved to node ${savedNode}. ${tail}` : tail;
}

export type SaveOutcome =
	| {kind: 'saved'; toast: string; rev: number}
	| {kind: 'partial'; message: string; retryNode: HANodeIndex; rev?: number}
	| {kind: 'unreachable'; message: string; node: HANodeIndex}
	| {kind: 'error'; message: string};

function trimReason(r: string | undefined): string {
	const s = (r || 'unknown error').trim().replace(/\.+$/, '');
	return s || 'unknown error';
}

/**
 * Turns a PUT /settings result into what the page shows. Never "saved" on a
 * failure. sentRev is the If-Match revision: a node only counts as saved when
 * the pair revision moved past it (a first-node failure keeps the old rev).
 */
export function saveOutcome(r: HASaveResult, sentRev: number): SaveOutcome {
	if (r.status === 200 && r.result === 'saved') return {kind: 'saved', toast: SAVED_TOAST, rev: r.settingsRev ?? 0};
	if (r.status === 502 && r.nodes?.length) {
		const failed = r.nodes.find(n => n.syncState !== 'in_sync' || n.error) ?? r.nodes[r.nodes.length - 1];
		const advanced = r.settingsRev != null && r.settingsRev > sentRev;
		const saved = advanced ? r.nodes.find(n => n.node !== failed.node && n.syncState === 'in_sync' && n.appliedRev === r.settingsRev) : undefined;
		return {
			kind: 'partial',
			message: partialSaveMessage(saved ? saved.node : null, failed.node, trimReason(failed.error?.message)),
			retryNode: failed.node,
			rev: r.settingsRev,
		};
	}
	if (r.status === 503 && (r.node === 1 || r.node === 2)) return {kind: 'unreachable', message: unreachableWarning(r.node), node: r.node};
	const code = r.error?.code;
	if (code === 'INVALID_SETTINGS') {
		const node = r.node ?? 2;
		return {kind: 'error', message: `Node ${node} refused the settings: ${trimReason(r.error?.message)}. Nothing changed.`};
	}
	if (code === 'REV_MISMATCH') return {kind: 'error', message: 'Someone saved this pair first. Reload to see the latest settings.'};
	if (code === 'PAIR_BUSY') return {kind: 'error', message: 'Another action is running on this pair. Try again in a moment.'};
	if (code === 'INVALID_REQUEST') return {kind: 'error', message: trimReason(r.error?.message) + '.'};
	return {kind: 'error', message: trimReason(r.error?.message) + '.'};
}

//---------------------------------------------------------
// Power and reboot (per node, Nodes table only)
//---------------------------------------------------------
export type HAPowerAction = 'shutdown' | 'reboot' | 'boot';
export interface PowerDialog {
	title: string;
	body: string;
	confirm: string;
	/** Red button: this action stops all traffic. */
	destructive: boolean;
	/** Send confirmAllTrafficStops (Q2). */
	confirmAllTrafficStops: boolean;
}

export function powerDialog(args: {
	action: HAPowerAction;
	node: HANodeIndex;
	name: string;
	holdsVip: boolean | null;
	otherNodeDown: boolean | null;
}): PowerDialog {
	const {action, node, name, holdsVip, otherNodeDown} = args;
	const other = (node === 1 ? 2 : 1) as HANodeIndex;
	if (action === 'boot') {
		return {title: `Power on node ${node}?`, body: `Power on node ${node} of ${name}?`, confirm: 'Power on', destructive: false, confirmAllTrafficStops: false};
	}
	const verb = action === 'shutdown' ? 'Power off' : 'Reboot';
	const gerund = action === 'shutdown' ? 'Powering off' : 'Rebooting';
	const title = `${verb} node ${node}?`;
	if (otherNodeDown === true) {
		return {title, body: `Node ${other} is down. ${gerund} node ${node} stops all traffic for ${name}.`, confirm: verb, destructive: true, confirmAllTrafficStops: true};
	}
	if (otherNodeDown === null) {
		return {
			title,
			body: `Node ${other}'s state is unknown. ${gerund} node ${node} may stop all traffic for ${name}.`,
			confirm: verb,
			destructive: true,
			confirmAllTrafficStops: true,
		};
	}
	if (holdsVip === true) {
		return {
			title,
			body:
				action === 'shutdown'
					? `Power off node ${node} of ${name}? It holds the VIP, so traffic moves to node ${other}.`
					: `Reboot node ${node} of ${name}? It holds the VIP, so traffic moves to node ${other} while it restarts.`,
			confirm: verb,
			destructive: false,
			confirmAllTrafficStops: false,
		};
	}
	if (holdsVip === false) {
		return {title, body: `${verb} node ${node} of ${name}? Traffic stays on node ${other}.`, confirm: verb, destructive: false, confirmAllTrafficStops: false};
	}
	return {
		title,
		body: `${verb} node ${node} of ${name}? ${CANT_TELL_VIP}`,
		confirm: verb,
		destructive: false,
		confirmAllTrafficStops: false,
	};
}

/** Which power button a node row shows. Nothing powers both at once. */
export function nodePowerButtons(linodeStatus: string | null): {power: 'off' | 'on' | null; reboot: boolean} {
	if (linodeStatus === 'running') return {power: 'off', reboot: true};
	if (linodeStatus === 'offline') return {power: 'on', reboot: false};
	return {power: null, reboot: false};
}

//---------------------------------------------------------
// Delete (pair only)
//---------------------------------------------------------
export function deleteDialogBody(name: string): string {
	return `Delete ${name}? This destroys both Linodes.`;
}

export function partialDeleteMessage(node: number, linodeId?: number): string {
	return linodeId != null ? `Node ${node} (Linode ${linodeId}) wasn't destroyed. Try Delete again.` : `Node ${node} wasn't destroyed. Try Delete again.`;
}

//---------------------------------------------------------
// Provision
//---------------------------------------------------------
export type NetworkModeId = 'legacy_private' | 'nat_vpc' | 'nat_vlan' | 'dsr_vlan';
export const NOT_AVAILABLE_YET = 'Not available yet';
export const HA_NOT_AVAILABLE = 'Not available yet for this mode';
export const HA_SWITCH_LABEL = 'High availability';
export const HA_SWITCH_HELPER = 'Two load balancers share one address. If one stops, the other takes over.';

/** The four network modes. Only stamped modes are enabled (server 422 is the control). */
export const NETWORK_MODES: {id: NetworkModeId; label: string; enabled: boolean; haEnabled: boolean; family: 'nat' | 'dsr'}[] = [
	{id: 'legacy_private', label: 'NAT, private network', enabled: true, haEnabled: true, family: 'nat'},
	{id: 'nat_vpc', label: 'NAT, VPC', enabled: true, haEnabled: true, family: 'nat'},
	{id: 'nat_vlan', label: 'NAT, VLAN', enabled: true, haEnabled: true, family: 'nat'},
	{id: 'dsr_vlan', label: 'DR, VLAN', enabled: true, haEnabled: true, family: 'dsr'},
];

export function networkModeOption(id: string) {
	return NETWORK_MODES.find(m => m.id === id) ?? null;
}

export function haSwitchState(mode: string): {disabled: boolean; helper: string} {
	const m = networkModeOption(mode);
	if (m && m.enabled && m.haEnabled) return {disabled: false, helper: HA_SWITCH_HELPER};
	return {disabled: true, helper: HA_NOT_AVAILABLE};
}

/** Wire mode for POST /oam/provision/jobs. The ha field is never sent (Q3). */
export function provisionMode(ha: boolean, family: 'nat' | 'dsr' = 'nat'): 'nat_onearm_ha' | 'nat_onearm_nonha' | 'dsr_ha' | 'dsr_nonha' {
	if (family === 'dsr') return ha ? 'dsr_ha' : 'dsr_nonha';
	return ha ? 'nat_onearm_ha' : 'nat_onearm_nonha';
}

/** Mode-specific fields shown after a pick. Account, region, plan, image, firewall and key are one set. */
export function modeFields(mode: string): string[] {
	switch (mode) {
		case 'nat_vpc':
			return ['VPC', 'Subnet'];
		case 'nat_vlan':
		case 'dsr_vlan':
			return ['VLAN', "Load balancer's VLAN address"];
		default:
			return [];
	}
}

export const NAT_PRIVATE_SENTENCE = 'NAT, one-arm. Private IPv4 and a public address, in the same region as the backends.';
export function modeSentence(mode: string): string {
	switch (mode) {
		case 'nat_vpc':
			return 'NAT, one-arm. Backends on a VPC subnet, in the same region.';
		case 'nat_vlan':
			return 'NAT, one-arm. Backends on a VLAN, in the same region.';
		case 'dsr_vlan':
			return 'Direct server return. Backends on the VLAN reply to clients directly. Each backend needs the VIP on loopback.';
		default:
			return NAT_PRIVATE_SENTENCE;
	}
}

export const HA_SENTENCE = "High availability. Two load balancers share node 1's public address.";
export const HA_PEER_FIREWALL_LINE = 'UDP 3784 and TCP 22222 between node 1 and node 2, on private addresses';
export const ASSIGNED_AT_CREATE = 'assigned at create';
export const VIP_PREVIEW = "node 1's public address, assigned at create";

export interface HAPreview {
	sentences: string[];
	nodes: {title: string; privateAddress: string}[];
	vip: string | null;
	extraFirewallLines: string[];
}

export function haPreview(mode: string, ha: boolean): HAPreview {
	if (!ha) return {sentences: [modeSentence(mode)], nodes: [], vip: null, extraFirewallLines: []};
	return {
		sentences: [modeSentence(mode), HA_SENTENCE],
		nodes: [
			{title: 'Node 1', privateAddress: ASSIGNED_AT_CREATE},
			{title: 'Node 2', privateAddress: ASSIGNED_AT_CREATE},
		],
		vip: VIP_PREVIEW,
		extraFirewallLines: [HA_PEER_FIREWALL_LINE],
	};
}

export const STEP_LABELS: Record<HAJobStepName, string> = {
	creating_node_1: 'Creating node 1',
	creating_node_2: 'Creating node 2',
	sharing_address: 'Sharing the address',
	ready: 'Ready',
};

/** Steps to show. A failed or running job never lists Ready as reached. */
export function visibleSteps(status: string, steps: HAJobStep[] | undefined): {label: string; state: HAJobStep['state']}[] {
	const order: HAJobStepName[] = ['creating_node_1', 'creating_node_2', 'sharing_address', 'ready'];
	return order
		.map(name => {
			const s = steps?.find(x => x.step === name);
			let state: HAJobStep['state'] = s?.state ?? 'pending';
			if (name === 'ready' && status !== 'succeeded') state = status === 'failed' ? 'not_reached' : 'pending';
			return {label: STEP_LABELS[name], state};
		})
		.filter(s => !(status === 'failed' && s.label === STEP_LABELS.ready));
}

export function haJobFailure(job: {status: string; failedStep?: string | null; failedNode?: number | null; error?: {message: string} | null}): string | null {
	if (job.status !== 'failed') return null;
	const reason = trimReason(job.error?.message);
	switch (job.failedStep) {
		case 'creating_node_1':
			return `Node 1 wasn't created: ${reason}. The pair isn't ready.`;
		case 'creating_node_2':
			return `Node ${job.failedNode === 1 ? 1 : 2} wasn't created: ${reason}. The pair isn't ready.`;
		case 'sharing_address':
			return `The address wasn't shared: ${reason}. The pair isn't ready.`;
		default:
			return `${reason}. The pair isn't ready.`;
	}
}

export const HA_READY = "This pair is ready. It's on Instances.";
